import React, { useEffect, useState } from 'react'

interface AICompleteProps {
  isVisible: boolean
  agentName: string
  message?: string
  onClose: () => void
}

const AIComplete: React.FC<AICompleteProps> = ({ isVisible, agentName, message, onClose }) => {
  const [show, setShow] = useState(false)
  const [progress, setProgress] = useState(100)

  useEffect(() => {
    if (isVisible) {
      setShow(true)
      setProgress(100)

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev <= 0) { clearInterval(progressInterval); return 0 }
          return prev - 2.5
        })
      }, 100)

      const closeTimer = setTimeout(() => {
        setShow(false)
        setTimeout(onClose, 300)
      }, 4000)

      return () => {
        clearTimeout(closeTimer)
        clearInterval(progressInterval)
      }
    } else {
      setShow(false)
    }
  }, [isVisible, onClose])

  if (!isVisible) return null

  return (
    <>
      <style>{`
        @keyframes odc-slideIn { from { transform: translateX(120%); opacity:0; } to { transform: translateX(0); opacity:1; } }
        @keyframes odc-bounceIn { 0% { transform: scale(0); } 60% { transform: scale(1.3); } 100% { transform: scale(1); } }
        @keyframes odc-complete-glow { 0%,100% { box-shadow: 0 0 20px rgba(6,148,148,0.2); } 50% { box-shadow: 0 0 40px rgba(6,148,148,0.5), 0 0 20px rgba(255,206,27,0.2); } }
      `}</style>

      <div style={{
        position: 'fixed', bottom: 32, right: 32,
        zIndex: 10000, maxWidth: 360, width: '90%',
        animation: show ? 'odc-slideIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' : 'none',
        transform: show ? 'translateX(0)' : 'translateX(120%)',
        transition: show ? 'none' : 'transform 0.3s ease',
      }}>
        <div style={{
          background: 'rgba(6,20,40,0.97)',
          border: '1px solid rgba(6,148,148,0.5)',
          borderRadius: 18,
          padding: '18px 20px 14px',
          overflow: 'hidden', position: 'relative',
          animation: 'odc-complete-glow 2s ease-in-out infinite',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}>
          {/* Top line */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 2,
            background: 'linear-gradient(90deg, #069494, #FFCE1B, #BE5103)',
          }} />

          {/* Close button */}
          <button onClick={() => { setShow(false); setTimeout(onClose, 300) }} style={{
            position: 'absolute', top: 12, right: 14,
            background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.3)', cursor: 'pointer',
            fontSize: 14, lineHeight: 1, padding: 4,
          }}>✕</button>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            {/* Icon */}
            <div style={{
              width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #069494, #0BB8B8)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22,
              animation: 'odc-bounceIn 0.5s cubic-bezier(0.34,1.56,0.64,1)',
              boxShadow: '0 0 20px rgba(6,148,148,0.5)',
            }}>
              ✅
            </div>

            <div style={{ flex: 1, paddingRight: 20 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 2,
                textTransform: 'uppercase', color: '#069494', marginBottom: 3,
              }}>
                Task Complete!
              </div>
              <div style={{
                fontSize: 15, fontWeight: 800, color: '#fff', marginBottom: 4,
                fontFamily: "'Space Grotesk', sans-serif",
              }}>
                {agentName} ✨
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.4 }}>
                {message || 'Your results are ready! Scroll down to view 👇'}
              </div>
            </div>
          </div>

          {/* Timer bar */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0,
            height: 3,
            background: 'linear-gradient(90deg, #069494, #FFCE1B)',
            width: `${progress}%`,
            transition: 'width 0.1s linear',
            borderRadius: '0 0 0 18px',
          }} />
        </div>
      </div>
    </>
  )
}

export default AIComplete
